import {createContext} from 'preact/compat';
import {useCallback, useMemo, useState} from 'preact/hooks';

import {PropsWithChildren} from 'utilities/props';
import {useStoredState} from 'utilities/dom';

import {isUSPostalCode} from './helpers';
import {DeliveryAddress} from './types';

export interface LocationContext {
  putPostalCode: (newPostalCode: string | null) => boolean;
  hasPostalCodeError: boolean;
  postalCode: string | null;
  resetPostalCodeError: () => void;
  defaultAddress: DeliveryAddress | null;
  setDefaultAddress: (value: DeliveryAddress | null) => void;
}

export const LocationContextImpl = createContext<LocationContext>({
  putPostalCode: () => false,
  hasPostalCodeError: false,
  postalCode: null,
  resetPostalCodeError: noop,
  defaultAddress: null,
  setDefaultAddress: noop,
});

export function LocationContextProvider({children}: PropsWithChildren) {
  const [defaultAddress, setDefaultAddress] = useStoredState(
    'delivery_promise_default_address',
  );
  const [postalCode, setPostalCode] = useStoredState(
    'delivery_promise_postal_code',
  );
  const [hasPostalCodeError, setHasPostalCodeError] = useState(false);
  const resetPostalCodeError = useCallback(
    () => setHasPostalCodeError(false),
    [],
  );

  const putPostalCode = useCallback(
    (newPostalCode: string | null) => {
      if (newPostalCode && !isUSPostalCode(newPostalCode)) {
        setHasPostalCodeError(true);
        return false;
      }

      resetPostalCodeError();
      setPostalCode(newPostalCode);
      return true;
    },
    [resetPostalCodeError, setPostalCode],
  );

  const context = useMemo(
    () => ({
      postalCode,
      putPostalCode,
      hasPostalCodeError,
      resetPostalCodeError,
      defaultAddress,
      setDefaultAddress,
    }),
    [
      hasPostalCodeError,
      postalCode,
      putPostalCode,
      resetPostalCodeError,
      defaultAddress,
      setDefaultAddress,
    ],
  );

  return (
    <LocationContextImpl.Provider value={context}>
      {children}
    </LocationContextImpl.Provider>
  );
}

function noop() {}
